import React, { useEffect, useState } from "react";
import {
  Typography,
  ButtonGroup,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  IconButton,
  Tooltip,
} from "@mui/material";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import { useContent } from "../context/ContentContext";
import API from "../services/API";
import SystemMapEditor from "../components/SystemMapEditor";
import SystemCreditsEditor from "../components/SystemCreditsEditor";
import { AppButton, AppTextField, Panel } from "../components/ui";
import {
  normalizeSystemCredits,
  validateSystemCredits,
} from "../utility/systemCredits";
import "./SystemsPage.scss";

const withUpdatedWebMapTimestamp = (webMap) => {
  if (!webMap) return null;

  if (typeof webMap === "string") {
    try {
      const parsedWebMap = JSON.parse(webMap);
      return parsedWebMap && typeof parsedWebMap === "object"
        ? {
            ...parsedWebMap,
            updatedAt: new Date().toISOString(),
          }
        : null;
    } catch {
      return null;
    }
  }

  if (typeof webMap === "object") {
    return {
      ...webMap,
      updatedAt: new Date().toISOString(),
    };
  }

  return null;
};

const SystemsPage = () => {
  const { mySystems } = useContent();
  const [systems, setSystems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState(null);
  const [editedSystems, setEditedSystems] = useState({});

  // States for sorting
  const [sortField, setSortField] = useState("name");
  const [sortOrder, setSortOrder] = useState("asc");

  // States for delete confirmation dialog
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [systemToDelete, setSystemToDelete] = useState(null);

  // Scroll to top when component mounts
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Update local state when context systems change.
  useEffect(() => {
    if (mySystems) {
      setSystems(mySystems);
      setLoading(false);
    }
  }, [mySystems]);

  // ----- Edit Existing System Handlers -----
  const handleEdit = async (systemId) => {
    try {
      const response = await API.get(`systems/${systemId}`, {
        requiresAuth: true,
      });
      const fullSystem = response.data;
      const credits = normalizeSystemCredits(fullSystem.credits);
      const creditsValidation = validateSystemCredits(credits);
      setEditingId(systemId);
      setEditedSystems((prev) => ({
        ...prev,
        [systemId]: {
          name: fullSystem.name || "",
          description: fullSystem.description || "",
          webMap: fullSystem.webMap ?? null,
          fullSystem: fullSystem,
          editorSection: "details",
          credits,
          creditsWereLoaded:
            Object.prototype.hasOwnProperty.call(fullSystem, "credits") &&
            fullSystem.credits != null,
          creditsValidation: {
            errors: {},
            warnings: creditsValidation.warnings,
          },
        },
      }));
    } catch (error) {
      console.error("Error fetching full system data:", error);
    }
  };

  const handleChange = (systemId, field, value) => {
    setEditedSystems((prev) => ({
      ...prev,
      [systemId]: {
        ...prev[systemId],
        [field]: value,
      },
    }));
  };

  const handleCancel = () => {
    setEditingId(null);
  };

  const handleCreditsChange = (systemId, credits) => {
    const validation = validateSystemCredits(credits);
    setEditedSystems((prev) => ({
      ...prev,
      [systemId]: {
        ...prev[systemId],
        credits,
        creditsValidation: {
          errors:
            Object.keys(prev[systemId].creditsValidation?.errors || {}).length > 0
              ? validation.errors
              : {},
          warnings: validation.warnings,
        },
      },
    }));
  };

  const handleSave = async (systemId, { includeCredits = false } = {}) => {
    const currentData = editedSystems[systemId];
    if (!currentData || !currentData.fullSystem) return;

    if (includeCredits) {
      const validation = validateSystemCredits(currentData.credits);
      setEditedSystems((prev) => ({
        ...prev,
        [systemId]: {
          ...prev[systemId],
          creditsValidation: validation,
        },
      }));
      if (!validation.isValid) return;
    }

    const fullSystemWithoutCredits = { ...currentData.fullSystem };
    delete fullSystemWithoutCredits.credits;
    const updatedSystem = {
      ...(currentData.creditsWereLoaded
        ? currentData.fullSystem
        : fullSystemWithoutCredits),
      ...(includeCredits
        ? { credits: normalizeSystemCredits(currentData.credits) }
        : {
            name: currentData.name,
            description: currentData.description,
            webMap: withUpdatedWebMapTimestamp(currentData.webMap),
          }),
    };

    try {
      await API.post("systems/", updatedSystem, { requiresAuth: true });
      setSystems((prev) =>
        prev.map((sys) =>
          sys.id === systemId
            ? {
                ...sys,
                name: updatedSystem.name,
                description: updatedSystem.description,
              }
            : sys
        )
      );
      setEditingId(null);
    } catch (error) {
      console.error("Error saving system:", error);
    }
  };

  // ----- Delete System Handlers -----
  const handleDeleteClick = (system) => {
    setSystemToDelete(system);
    setDeleteConfirmOpen(true);
  };

  const confirmDelete = async () => {
    if (!systemToDelete) return;
    try {
      await API.get(`systems/${systemToDelete.id}/delete`, {
        requiresAuth: true,
      });
      setSystems((prev) => prev.filter((sys) => sys.id !== systemToDelete.id));
    } catch (error) {
      console.error("Error deleting system:", error);
    } finally {
      setDeleteConfirmOpen(false);
      setSystemToDelete(null);
    }
  };

  const cancelDelete = () => {
    setDeleteConfirmOpen(false);
    setSystemToDelete(null);
  };

  // ----- Sorting -----
  const sortedSystems = [...systems].sort((a, b) => {
    const aValue = a[sortField] || "";
    const bValue = b[sortField] || "";
    if (sortField === "name") {
      return sortOrder === "asc"
        ? aValue.localeCompare(bValue)
        : bValue.localeCompare(aValue);
    } else if (sortField === "dateModified") {
      return sortOrder === "asc"
        ? new Date(aValue) - new Date(bValue)
        : new Date(bValue) - new Date(aValue);
    }
    return 0;
  });

  if (loading) {
    return (
      <div className="systemsPage">
        <div className="systemsLoading">Loading...</div>
      </div>
    );
  }

  return (
    <div className="systemsPage">
      <Typography className="systemsTitle" variant="h4">
        Systems
      </Typography>

      {/* Sorting Controls */}
      <div className="systemsSortRow">
        <div className="systemsSortGroup">
          <ButtonGroup size="small" aria-label="sort systems">
            <AppButton
              kind={sortField === "name" ? "primary" : "secondary"}
              onClick={() => setSortField("name")}
            >
              Sort: Name
            </AppButton>
            <AppButton
              kind={sortField === "dateModified" ? "primary" : "secondary"}
              onClick={() => setSortField("dateModified")}
            >
              Sort: Date Updated
            </AppButton>
          </ButtonGroup>
        </div>

        <Tooltip title={sortOrder === "asc" ? "Ascending" : "Descending"}>
          <IconButton
            className="systemsSortArrow"
            size="small"
            onClick={() => setSortOrder((prev) => (prev === "asc" ? "desc" : "asc"))}
          >
            {sortOrder === "asc" ? <ArrowUpwardIcon /> : <ArrowDownwardIcon />}
          </IconButton>
        </Tooltip>
      </div>

      {/* Systems List */}
      <div className="systemsList app-row-list">
        {sortedSystems.map((system) => (
          <Panel key={system.id} className="systemCard" hoverable>
            {editingId === system.id ? (
              <div className="systemEditForm">
                <div className="systemEditorTabs" role="tablist">
                  <AppButton
                    kind="secondary"
                    selected={
                      editedSystems[system.id].editorSection === "details"
                    }
                    role="tab"
                    aria-selected={
                      editedSystems[system.id].editorSection === "details"
                    }
                    onClick={() =>
                      handleChange(system.id, "editorSection", "details")
                    }
                  >
                    System details
                  </AppButton>
                  <AppButton
                    kind="secondary"
                    selected={
                      editedSystems[system.id].editorSection === "credits"
                    }
                    role="tab"
                    aria-selected={
                      editedSystems[system.id].editorSection === "credits"
                    }
                    onClick={() =>
                      handleChange(system.id, "editorSection", "credits")
                    }
                  >
                    Credits &amp; Licensing
                  </AppButton>
                </div>

                {editedSystems[system.id].editorSection === "credits" ? (
                  <>
                    <SystemCreditsEditor
                      systemName={editedSystems[system.id].fullSystem.name}
                      systemDescription={
                        editedSystems[system.id].fullSystem.description
                      }
                      value={editedSystems[system.id].credits}
                      onChange={(credits) =>
                        handleCreditsChange(system.id, credits)
                      }
                      errors={
                        editedSystems[system.id].creditsValidation?.errors
                      }
                      warnings={
                        editedSystems[system.id].creditsValidation?.warnings
                      }
                    />
                    <div className="systemEditActions">
                      <AppButton
                        onClick={() =>
                          handleSave(system.id, { includeCredits: true })
                        }
                      >
                        Save credits
                      </AppButton>
                      <AppButton kind="ghost" onClick={handleCancel}>
                        Cancel
                      </AppButton>
                    </div>
                  </>
                ) : (
                  <>
                    <div className="systemEditFields">
                      <AppTextField
                        label="Display Name"
                        value={editedSystems[system.id].name}
                        onChange={(e) =>
                          handleChange(system.id, "name", e.target.value)
                        }
                        fullWidth
                      />
                      <AppTextField
                        label="Description"
                        value={editedSystems[system.id].description}
                        onChange={(e) =>
                          handleChange(system.id, "description", e.target.value)
                        }
                        fullWidth
                      />
                    </div>

                    <SystemMapEditor
                      system={editedSystems[system.id].fullSystem}
                      value={editedSystems[system.id].webMap}
                      onChange={(webMap) =>
                        handleChange(system.id, "webMap", webMap)
                      }
                    />

                    <div className="systemEditActions">
                      <AppButton onClick={() => handleSave(system.id)}>
                        Save
                      </AppButton>
                      <AppButton kind="ghost" onClick={handleCancel}>
                        Cancel
                      </AppButton>
                    </div>
                  </>
                )}
              </div>
            ) : (
              <div className="systemCardContent">
                <div className="systemCardInfo">
                  <div className="systemCardName">{system.name || "No Name"}</div>
                  {system.status?.type && (
                    <div className="systemCardStatus">{system.status?.type}</div>
                  )}
                </div>

                <div className="systemCardDesc">
                  {system.description || "No Description"}
                </div>

                <div className="systemCardActions">
                  <AppButton
                    className="systemCardBtn"
                    kind="secondary"
                    onClick={() => handleEdit(system.id)}
                  >
                    Edit
                  </AppButton>
                  <AppButton
                    className="systemCardBtn"
                    kind="danger"
                    onClick={() => handleDeleteClick(system)}
                  >
                    Delete
                  </AppButton>
                </div>
              </div>
            )}
          </Panel>
        ))}
      </div>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteConfirmOpen} onClose={cancelDelete}>
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete{" "}
            {systemToDelete ? systemToDelete.name || "this system" : "this system"}?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <AppButton kind="ghost" onClick={cancelDelete}>
            Cancel
          </AppButton>
          <AppButton kind="danger" onClick={confirmDelete}>
            Delete
          </AppButton>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default SystemsPage;
